import * as React from 'react'
import { useGetTabularConfigBuildQuery } from '@axilon-apps/domains-ontology-ui-state'
import { Navigate, useParams } from 'react-router-dom'
import { useDebugMode } from '../../lib/debug-mode'
import { COMPACT_PAGE } from '../IngestPage/shared/compact'
import { ConversationToolCallsCard } from '../IngestPage/shared/ConversationToolCallsCard'
import { tabularConfigBuildPath } from '../TabularIngestJobPage/route'
import { configBuildPill } from '../TabularIngestJobPage/tabularJobPill'
import { ConfigSpine } from './ConfigSpine'
import { isBuildInFlight } from './util/configView'
import { reasonText } from './util/formatMeta'

/**
 * `/ingest/configs/:name/builds/:buildJobId/tool-calls` — the builder's tool
 * calls for one build, full width, as the custom agent run page shows them.
 * Debug mode only: without it the page sends you to the build itself. While
 * the build is in flight the list follows the run.
 */
export default function BuildToolCallsPage() {
  const debugMode = useDebugMode()
  const { name = '', buildJobId = '' } = useParams<{ name: string; buildJobId: string }>()
  const [live, setLive] = React.useState(true)
  const build = useGetTabularConfigBuildQuery(buildJobId, {
    skip: !debugMode || !buildJobId,
    refetchOnMountOrArgChange: true
  })
  React.useEffect(() => {
    if (build.data) setLive(isBuildInFlight(build.data))
  }, [build.data])

  if (!debugMode) return <Navigate to={tabularConfigBuildPath(name, buildJobId)} replace />

  const detail = build.data
  const backTo = tabularConfigBuildPath(name, buildJobId)
  return (
    <div className={COMPACT_PAGE}>
      <ConfigSpine
        name={name}
        backTo={backTo}
        backLabel="Back to build"
        pill={detail ? configBuildPill(detail.state, detail.result.phase) : undefined}
        meta={
          detail
            ? [`draft v${detail.proposedVersion}`, reasonText(detail.reason), 'tool calls'].join(
                ' · '
              )
            : undefined
        }
      />
      {build.isError ? (
        <p role="alert" className="text-xs text-st-down">
          Could not load this build.
        </p>
      ) : !detail ? (
        <p className="text-xs text-ink-3">Loading build…</p>
      ) : detail.conversationId ? (
        <ConversationToolCallsCard
          conversationId={detail.conversationId}
          live={live}
          settledKey={detail.completedAt ?? detail.updatedAt}
          emptyText="The builder has not called any tools yet."
          loadErrorText="Failed to load the builder's tool calls."
          testId="build-tool-calls"
        />
      ) : (
        <p className="text-xs text-ink-3" data-testid="conversation-tool-calls-unavailable">
          {live
            ? 'Waiting for the build to report its conversation…'
            : 'No conversation was recorded for this build (the conversation store was unavailable when it started), so its tool calls are not available.'}
        </p>
      )}
    </div>
  )
}
